import React, { useState, useEffect } from "react";
import { Box, Stack, Typography, IconButton } from "@mui/material";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import dayjs from "dayjs";
import { T } from "../../constants/todoTokens";
import { buildGrid } from "./helpers";

const MINI_DAYS = ["S", "M", "T", "W", "T", "F", "S"];

const MiniCalendar = ({ activeDate, calView, taskMap, onSelectDay }) => {
  const [miniMonth, setMiniMonth] = useState(() => activeDate.startOf("month"));
  const todayKey = dayjs().format("YYYY-MM-DD");

  // Keep mini calendar in sync when active date's month changes externally
  useEffect(() => {
    if (!activeDate.isSame(miniMonth, "month")) {
      setMiniMonth(activeDate.startOf("month"));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDate]);

  const cells = buildGrid(miniMonth);

  // Determine highlight state for a cell
  const getCellStyle = (date, current) => {
    const key      = date.format("YYYY-MM-DD");
    const isToday  = key === todayKey;
    const isActive = key === activeDate.format("YYYY-MM-DD");

    const inWeek =
      calView === "week" &&
      !date.isBefore(activeDate.startOf("week"), "day") &&
      !date.isAfter(activeDate.startOf("week").add(6, "day"), "day");

    const inMonth =
      calView === "month" &&
      date.month() === activeDate.month() &&
      date.year()  === activeDate.year();

    if (isToday)
      return { circle: T.primary, label: "#fff", weight: 800 };
    if (calView === "day" && isActive)
      return { circle: T.primaryLight, label: T.primary, weight: 800, border: T.primary };
    if (inWeek)
      return { circle: T.primaryLight, label: T.primaryDark, weight: 700 };
    if (inMonth && !isToday)
      return { circle: "transparent", label: T.primary, weight: 700 };
    return {
      circle: "transparent",
      label: current ? T.text : T.textMuted,
      weight: current ? 500 : 400,
    };
  };

  return (
    <Box
      sx={{
        bgcolor: T.surface,
        borderRadius: "14px",
        border: `1px solid ${T.border}`,
        boxShadow: T.shadow.sm,
        p: "14px 12px 12px",
        userSelect: "none",
      }}
    >
      {/* Mini header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography sx={{ fontSize: "12px", fontWeight: 800, color: T.text }}>
          {miniMonth.format("MMM YYYY")}
        </Typography>
        <Stack direction="row" gap={0}>
          <IconButton
            size="small"
            onClick={() => setMiniMonth((m) => m.subtract(1, "month"))}
            sx={{ p: "2px", color: T.textMuted, "&:hover": { color: T.primary } }}
          >
            <FiChevronLeft size={13} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => setMiniMonth((m) => m.add(1, "month"))}
            sx={{ p: "2px", color: T.textMuted, "&:hover": { color: T.primary } }}
          >
            <FiChevronRight size={13} />
          </IconButton>
        </Stack>
      </Stack>

      {/* Day-name row */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", mb: "4px" }}>
        {MINI_DAYS.map((d, i) => (
          <Box key={i} sx={{ display: "flex", justifyContent: "center" }}>
            <Typography sx={{ fontSize: "10px", fontWeight: 700, color: T.textMuted }}>
              {d}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Day cells */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "2px" }}>
        {cells.map(({ date, current }, i) => {
          const key      = date.format("YYYY-MM-DD");
          const hasTasks = (taskMap[key] || []).length > 0;
          const cs       = getCellStyle(date, current);
          const isToday  = key === todayKey;

          return (
            <Box
              key={i}
              onClick={() => onSelectDay(date)}
              sx={{
                width: 28, height: 28,
                display: "flex", alignItems: "center", justifyContent: "center",
                borderRadius: "50%",
                bgcolor: cs.circle,
                border: cs.border ? `1.5px solid ${cs.border}` : "none",
                cursor: "pointer",
                position: "relative",
                transition: "background 0.12s",
                "&:hover": {
                  bgcolor: isToday ? T.primaryDark : T.primaryLight,
                },
              }}
            >
              <Typography sx={{ fontSize: "11px", fontWeight: cs.weight, color: cs.label, lineHeight: 1 }}>
                {date.date()}
              </Typography>

              {/* Dot indicator — task exists on this day */}
              {hasTasks && !isToday && (
                <Box
                  sx={{
                    position: "absolute",
                    bottom: "3px",
                    width: 4, height: 4,
                    borderRadius: "50%",
                    bgcolor: T.primary,
                    opacity: 0.7,
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>

      {/* Jump to today */}
      <Box
        onClick={() => onSelectDay(dayjs())}
        sx={{
          mt: 1.5, pt: 1.5,
          borderTop: `1px solid ${T.border}`,
          textAlign: "center",
          cursor: "pointer",
          "&:hover": { "& span": { color: T.primaryDark } },
        }}
      >
        <Box component="span" sx={{ fontSize: "11px", fontWeight: 700, color: T.primary, transition: "color 0.12s" }}>
          Jump to today
        </Box>
      </Box>
    </Box>
  );
};

export default React.memo(MiniCalendar);
